import { useId, useState } from 'react';
import { T, useGT } from 'gt-react';
import type {
  WorkbookColumnType,
  WorkbookValidation,
  WorkbookValidationKind,
  WorkbookValidationSeverity,
} from '@erp/shared-types';
import { formulaBodyText, storedFormula } from '@erp/workbooks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useValidationText } from './useValidationText';
import { newValidation, validationKindsFor } from './worksheetValidations';
import { Checkbox } from '@/components/ui/checkbox';
import {
  useOtherSheets,
  type WorkbookSheetChoice,
} from './workbookSheetChoicesContext';

type ColumnType = WorkbookColumnType;

const SEVERITIES: readonly WorkbookValidationSeverity[] = ['error', 'warning'];
/** Examples in the Workbook's own syntax, the same in every language. */
const PATTERN_EXAMPLE = '^[A-Z]{3}-\\d+$';
const FORMULA_EXAMPLE = '=[@Qty] > 0';

/** Whether a draft says enough to be checked: a list, a bound, a pattern or a formula. */
/** A range's example bounds, in the form a Date or a Date and time column reads. */
const RANGE_PLACEHOLDERS: Partial<
  Record<WorkbookColumnType, { min: string; max: string }>
> = {
  date: { min: '2026-01-01', max: '2026-12-31' },
  datetime: { min: '2026-01-01T00:00:00Z', max: '2026-12-31T23:59:59Z' },
};

export function validationDraftComplete(draft: WorkbookValidation): boolean {
  switch (draft.kind) {
    case 'accepted-values':
      return draft.values.length > 0;
    case 'range':
      return Boolean(draft.min || draft.max);
    case 'pattern':
      return draft.pattern.length > 0;
    case 'formula':
      return draft.expression.trim().length > 0;
    case 'in-sheet':
      return Boolean(draft.sheetId && draft.matchColumn);
    default:
      return true;
  }
}

/** Accepted values as typed, one per line; blank lines are ignored. */
export function acceptedValues(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/\r\n|\r|\n/)
        .map((line) => line.trim())
        .filter(Boolean),
    ),
  ];
}

/**
 * Adds or edits one Validation of a column: its kind, what the kind needs,
 * and whether a failure is an error or a warning.
 */
export function WorksheetValidationForm({
  sheetId,
  column,
  type,
  initial,
  onSave,
  onCancel,
}: {
  /** The Worksheet the rule is on: In another sheet compares it with the others. */
  sheetId: string;
  column: string;
  type: ColumnType;
  /** The rule being edited; absent to add one. */
  initial?: WorkbookValidation;
  onSave: (validation: WorkbookValidation) => void;
  onCancel: () => void;
}) {
  const gt = useGT();
  const text = useValidationText();
  const others = useOtherSheets(sheetId);
  const kinds = validationKindsFor(type, others.length > 0);
  const [draft, setDraft] = useState<WorkbookValidation>(
    () => initial ?? newValidation(crypto.randomUUID(), column, 'not-empty'),
  );
  // What the user typed, kept as typed; the rule stores its reading of it.
  const [values, setValues] = useState(
    draft.kind === 'accepted-values' ? draft.values.join('\n') : '',
  );
  const [formula, setFormula] = useState(
    draft.kind === 'formula' ? `=${formulaBodyText(draft.expression)}` : '',
  );
  const setKind = (kind: WorkbookValidationKind) => {
    const next = newValidation(
      draft.id,
      column,
      kind,
      others[0] && matchIn(others[0], column),
    );
    setValues('');
    setFormula(
      next.kind === 'formula' ? `=${formulaBodyText(next.expression)}` : '',
    );
    // In another sheet relates sheets: a warning unless the user says otherwise.
    setDraft(
      next.kind === 'in-sheet' ? next : { ...next, severity: draft.severity },
    );
  };
  return (
    <form
      className="flex flex-col gap-2 rounded-md border p-2"
      aria-label={
        initial
          ? gt('Edit validation', { context: 'worksheet validation form' })
          : gt('Add validation', { context: 'worksheet validation form' })
      }
      onSubmit={(event) => {
        event.preventDefault();
        if (validationDraftComplete(draft)) onSave(draft);
      }}
    >
      <div className="flex gap-2">
        <Select
          value={draft.kind}
          onValueChange={(value) => {
            const kind = kinds.find((item) => item === value);
            if (kind) setKind(kind);
          }}
        >
          <SelectTrigger
            className="h-8 flex-1"
            aria-label={gt('Rule', { context: 'worksheet validation kind' })}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {kinds.map((kind) => (
              <SelectItem key={kind} value={kind}>
                {text.kind(kind)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={draft.severity}
          onValueChange={(value) => {
            const severity = SEVERITIES.find((item) => item === value);
            if (severity) setDraft({ ...draft, severity });
          }}
        >
          <SelectTrigger
            className="h-8 w-28"
            aria-label={gt('When it fails', {
              context: 'worksheet validation severity',
            })}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SEVERITIES.map((severity) => (
              <SelectItem key={severity} value={severity}>
                {text.severity(severity)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {draft.kind === 'in-sheet' && (
        <InSheetFields draft={draft} others={others} setDraft={setDraft} />
      )}
      <ValidationKindFields
        draft={draft}
        type={type}
        values={values}
        formula={formula}
        setDraft={setDraft}
        setValues={setValues}
        setFormula={setFormula}
      />
      <div className="flex justify-end gap-1">
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          <T context="dialog cancel button">Cancel</T>
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={!validationDraftComplete(draft)}
        >
          {initial ? (
            <T context="worksheet validation form save">Save</T>
          ) : (
            <T context="worksheet validation form add">Add</T>
          )}
        </Button>
      </div>
    </form>
  );
}

/** The other Worksheet's column a value is matched in: the same name when it has one. */
function matchIn(
  other: WorkbookSheetChoice,
  column: string,
): { sheetId: string; matchColumn: string } {
  const lower = column.toLowerCase();
  return {
    sheetId: other.id,
    matchColumn:
      other.columns.find((name) => name.toLowerCase() === lower) ??
      other.columns[0] ??
      column,
  };
}

/** In another sheet: which Worksheet and column each value must appear in, and how often. */
function InSheetFields({
  draft,
  others,
  setDraft,
}: {
  draft: Extract<WorkbookValidation, { kind: 'in-sheet' }>;
  others: readonly WorkbookSheetChoice[];
  setDraft: (draft: WorkbookValidation) => void;
}) {
  const gt = useGT();
  const ids = useId();
  const other = others.find((item) => item.id === draft.sheetId);
  return (
    <div className="flex flex-col gap-2 text-xs">
      <div className="flex gap-2">
        <Select
          value={draft.sheetId}
          onValueChange={(value) => {
            const next = others.find((item) => item.id === value);
            if (next) setDraft({ ...draft, ...matchIn(next, draft.column) });
          }}
        >
          <SelectTrigger
            className="h-8 flex-1"
            aria-label={gt('Worksheet', {
              context: 'worksheet validation in another sheet',
            })}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {others.map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={draft.matchColumn}
          onValueChange={(matchColumn) => setDraft({ ...draft, matchColumn })}
        >
          <SelectTrigger
            className="h-8 flex-1"
            aria-label={gt('Column', {
              context: 'worksheet validation in another sheet',
            })}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(other?.columns ?? []).map((name) => (
              <SelectItem key={name} value={name}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <label className="flex items-center gap-2" htmlFor={`${ids}-once`}>
        <Checkbox
          id={`${ids}-once`}
          checked={draft.exactlyOnce}
          onCheckedChange={(checked) =>
            setDraft({ ...draft, exactlyOnce: checked === true })
          }
        />
        <T context="worksheet validation in another sheet">
          Exactly once (no row matched twice)
        </T>
      </label>
    </div>
  );
}

/** A range's bounds, each optional, with examples in the column type's form. */
function RangeFields({
  draft,
  type,
  setDraft,
}: {
  draft: Extract<WorkbookValidation, { kind: 'range' }>;
  type: ColumnType;
  setDraft: (draft: WorkbookValidation) => void;
}) {
  const examples = RANGE_PLACEHOLDERS[type] ?? { min: '0', max: '100' };
  return (
    <div className="flex gap-2">
      <label className="flex flex-1 flex-col gap-1 text-xs">
        <T context="worksheet validation range">Smallest</T>
        <Input
          className="h-8"
          value={draft.min ?? ''}
          placeholder={examples.min}
          onChange={(event) =>
            setDraft({ ...draft, min: event.target.value || undefined })
          }
        />
      </label>
      <label className="flex flex-1 flex-col gap-1 text-xs">
        <T context="worksheet validation range">Largest</T>
        <Input
          className="h-8"
          value={draft.max ?? ''}
          placeholder={examples.max}
          onChange={(event) =>
            setDraft({ ...draft, max: event.target.value || undefined })
          }
        />
      </label>
    </div>
  );
}

/** What the chosen kind needs: accepted values, bounds, a pattern or a formula. */
function ValidationKindFields({
  draft,
  type,
  values,
  formula,
  setDraft,
  setValues,
  setFormula,
}: {
  draft: WorkbookValidation;
  type: ColumnType;
  values: string;
  formula: string;
  setDraft: (draft: WorkbookValidation) => void;
  setValues: (text: string) => void;
  setFormula: (text: string) => void;
}) {
  const ids = useId();
  return (
    <>
      {draft.kind === 'accepted-values' && (
        <label
          className="flex flex-col gap-1 text-xs"
          htmlFor={`${ids}-values`}
        >
          <T context="worksheet validation accepted values">
            Accepted values, one per line
          </T>
          <Textarea
            id={`${ids}-values`}
            rows={4}
            value={values}
            onChange={(event) => {
              setValues(event.target.value);
              setDraft({
                ...draft,
                values: acceptedValues(event.target.value),
              });
            }}
          />
        </label>
      )}
      {draft.kind === 'range' && (
        <RangeFields draft={draft} type={type} setDraft={setDraft} />
      )}
      {draft.kind === 'pattern' && (
        <label className="flex flex-col gap-1 text-xs">
          <T context="worksheet validation pattern">
            Regular expression; add ^ and $ to match the whole value
          </T>
          <Input
            className="h-8 font-mono"
            value={draft.pattern}
            placeholder={PATTERN_EXAMPLE}
            onChange={(event) =>
              setDraft({ ...draft, pattern: event.target.value })
            }
          />
        </label>
      )}
      {draft.kind === 'formula' && (
        <label className="flex flex-col gap-1 text-xs">
          <T context="worksheet validation formula">
            A formula every row makes TRUE
          </T>
          <Input
            className="h-8 font-mono"
            value={formula}
            placeholder={FORMULA_EXAMPLE}
            onChange={(event) => {
              setFormula(event.target.value);
              setDraft({
                ...draft,
                expression: storedFormula(event.target.value, ''),
              });
            }}
          />
        </label>
      )}
    </>
  );
}
